import React, { useState, useEffect, useCallback } from 'react';
import { CapturesView } from './CapturesView';
import { ScopeSelector } from './ScopeSelector';
import BookmarksView from '../Bookmarks/BookmarksView';
import { TextSizeStepper } from '../Common/TextSizeStepper';
import { usePanelZoom, PANEL_ZOOM } from '../../hooks/usePanelZoom';
import { CLOSE_COMPENDIUM } from '../../utils/writerCopy';
import { capturesApi } from '../../api/captures';
import { SAVED_IDEAS_CHANGED } from './savedIdeasSync';
import './Captures.css';

const DEFAULT_WIDTH = 360;
const MIN_WIDTH = 280;
const MAX_WIDTH = 500;

export function CapturePanel({
  storyId,
  projectId,
  project,
  onClose,
  onNavigateToSession,
  width,
  onWidthChange,
  initialTab = 'actions',
}) {
  const [activeTab, setActiveTab] = useState(initialTab); // 'actions' | 'ideas' | 'bookmarks'

  useEffect(() => {
    if (initialTab === 'actions' || initialTab === 'ideas' || initialTab === 'bookmarks') {
      setActiveTab(initialTab);
    }
  }, [initialTab]);
  const [selectedScope, setSelectedScope] = useState(() => {
    // Default to story if available, otherwise project
    if (storyId) {
      return { type: 'story', id: storyId };
    }
    return { type: 'project', id: projectId };
  });
  const [ideaCount, setIdeaCount] = useState(0);
  const [isResizing, setIsResizing] = useState(false);
  const [panelZoom, setPanelZoom] = usePanelZoom();

  // Update scope when context changes
  useEffect(() => {
    if (storyId) {
      setSelectedScope({ type: 'story', id: storyId });
    } else if (projectId) {
      setSelectedScope({ type: 'project', id: projectId });
    }
  }, [storyId, projectId]);

  const refreshIdeaCount = useCallback(async () => {
    if (!selectedScope?.id) return;
    const params = { type: 'idea', limit: 1, offset: 0 };
    try {
      const response = selectedScope.type === 'story'
        ? await capturesApi.listStory(selectedScope.id, params)
        : await capturesApi.listProject(selectedScope.id, params);
      setIdeaCount(response?.pagination?.total ?? (response?.data?.length || 0));
    } catch {
      // The tab keeps the last count. A failed refresh is not a new sentence.
    }
  }, [selectedScope]);

  useEffect(() => {
    refreshIdeaCount();
    const onChanged = () => { refreshIdeaCount(); };
    window.addEventListener(SAVED_IDEAS_CHANGED, onChanged);
    return () => window.removeEventListener(SAVED_IDEAS_CHANGED, onChanged);
  }, [refreshIdeaCount]);

  // Resize handlers
  const handleResizeStart = useCallback((e) => {
    e.preventDefault();
    setIsResizing(true);
  }, []);

  useEffect(() => {
    if (!isResizing) return;

    const handleMouseMove = (e) => {
      const newWidth = window.innerWidth - e.clientX;
      const clamped = Math.max(MIN_WIDTH, Math.min(MAX_WIDTH, newWidth));
      onWidthChange?.(clamped);
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isResizing, onWidthChange]);

  return (
    <div className="capture-panel" style={{ width: width || DEFAULT_WIDTH }}>
      {/* Resize handle */}
      <div
        className={`capture-panel__resize-handle ${isResizing ? 'capture-panel__resize-handle--active' : ''}`}
        onMouseDown={handleResizeStart}
      />

      {/* Header */}
      <div className="capture-panel__header">
        <h3 className="capture-panel__title">Compendium</h3>
        <TextSizeStepper
          value={panelZoom}
          onChange={setPanelZoom}
          min={PANEL_ZOOM.min}
          max={PANEL_ZOOM.max}
          step={PANEL_ZOOM.step}
          label="Panel text size"
        />
        <button type="button" onClick={onClose} className="capture-panel__close" aria-label={CLOSE_COMPENDIUM}>
          <span>X</span>
        </button>
      </div>

      {/* Magnified body (Item 2: shared panel zoom). */}
      <div className="panel-zoom" style={{ zoom: panelZoom / 100 }}>
      {/* Tabs */}
      <div className="capture-panel__tabs">
        <button
          className={`capture-panel__tab ${activeTab === 'actions' ? 'capture-panel__tab--active' : ''}`}
          onClick={() => setActiveTab('actions')}
        >
          To Do
        </button>
        <button
          className={`capture-panel__tab ${activeTab === 'ideas' ? 'capture-panel__tab--active' : ''}`}
          onClick={() => setActiveTab('ideas')}
        >
          Saved ideas {ideaCount > 0 && <span className="capture-panel__count">({ideaCount})</span>}
        </button>
        <button
          className={`capture-panel__tab ${activeTab === 'bookmarks' ? 'capture-panel__tab--active' : ''}`}
          onClick={() => setActiveTab('bookmarks')}
        >
          Bookmarks
        </button>
      </div>

      {/* Bookmarks tab: message-anchored captures with their own folder structure
          (bible-consolidation moved them here). To Do / Ideas keep the capture
          scope selector; bookmarks derive context from story/project directly. */}
      {activeTab === 'bookmarks' ? (
        <div className="capture-panel__content">
          <BookmarksView storyId={storyId} projectId={projectId} />
        </div>
      ) : (
        <>
          {/* Scope Selector */}
          <ScopeSelector
            currentScope={selectedScope}
            project={project}
            storyId={storyId}
            onChange={setSelectedScope}
          />

          {/* Content */}
          <div className="capture-panel__content">
            <CapturesView
              scope={selectedScope}
              onNavigateToSession={onNavigateToSession}
              captureType={activeTab}
            />
          </div>
        </>
      )}
      </div>
    </div>
  );
}

export default CapturePanel;
